import { useState, useCallback, useEffect, useRef } from "react";
import {
  useGoogleMapsAutocomplete,
  AddressData,
  AddressPrediction,
} from "@/hooks/useGoogleMapsAutocomplete";
import { GeoCoords } from "@/hooks/useGeolocation";
import { debounce } from "@/utils/misc";

const DEBOUNCE_INTERVAL_MS = 300;
const MIN_SEARCH_LENGTH = 3;

export interface UseAddressAutocompleteOptions {
  /** Search radius in meters (default: 50000 = 50km) */
  radiusMeters?: number;
}

export interface UseAddressAutocompleteReturn {
  addressPredictions: AddressPrediction[];
  loading: boolean;
  fetchAddressPredictions: (
    value: string,
    locationBias?: GeoCoords | null
  ) => void;
  addressInput: string;
  fetchAddressDetails: (addressId: string) => Promise<AddressData | null>;
  reverseGeocode: (coords: GeoCoords) => Promise<AddressData | null>;
  geocodeAddress: (address: string) => Promise<AddressData | null>;
  clearAddressPredictions: () => void;
}

/**
 * Generic Address Autocomplete Hook
 * Provider-agnostic - doesn't know we're using Google Maps
 * Manages UI state, debouncing, and service calls
 *
 * To switch providers: Only change the import and service method names
 */
export const useAddressAutocomplete = (
  options: UseAddressAutocompleteOptions = {}
): UseAddressAutocompleteReturn => {
  const { radiusMeters = 5000 } = options;

  const [addressPredictions, setAddressPredictions] = useState<
    AddressPrediction[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [addressInput, setAddressInput] = useState("");

  // Service layer - this is the only coupling to Google
  // To switch providers, change this import and adapt the method calls
  const {
    isLoaded,
    createAutocompleteSession,
    searchAddresses,
    fetchAddressDetails: fetchAddressDetailsFromService,
    reverseGeocode: reverseGeocodeFromService,
    geocodeAddress: geocodeAddressFromService,
  } = useGoogleMapsAutocomplete();

  // Session management (provider might not need this, but Google does)
  const sessionRef = useRef<any>(null);
  const debouncedFetchRef = useRef<ReturnType<typeof debounce> | null>(null);

  /**
   * Starts a new search session
   * Some providers (like Google) use sessions for billing
   */
  const startNewSession = useCallback(async () => {
    sessionRef.current = await createAutocompleteSession();

    if (!sessionRef.current) {
      console.error("Failed to create new autocomplete session");
    }

    return sessionRef.current;
  }, [createAutocompleteSession]);

  /**
   * Initialize session when service loads
   */
  useEffect(() => {
    if (isLoaded && !sessionRef.current) {
      startNewSession();
    }
  }, [isLoaded, startNewSession]);

  /**
   * Core search logic
   */
  const executeFetch = useCallback(
    async (value: string, locationBias?: GeoCoords | null) => {
      if (!value || value.trim().length < MIN_SEARCH_LENGTH) {
        setAddressPredictions([]);
        setLoading(false);
        setAddressInput(value);
        return;
      }

      if (!isLoaded) {
        console.error("Google API Service not loaded yet");
        setLoading(false);
        return;
      }

      setLoading(true);
      setAddressInput(value);

      try {
        // Ensure we have a session
        if (!sessionRef.current) {
          await startNewSession();
        }

        const results = await searchAddresses(value, {
          sessionToken: sessionRef.current,
          locationBias,
          radiusMeters,
        });

        setAddressPredictions(results || []);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch address predictions:", error);
        setLoading(false);
        setAddressPredictions([]);
      }
    },
    [isLoaded, searchAddresses, startNewSession, radiusMeters]
  );

  /**
   * Keep a ref to the latest executeFetch so the debounced function
   * always calls the current version without needing to be recreated.
   */
  const executeFetchRef = useRef(executeFetch);
  useEffect(() => {
    executeFetchRef.current = executeFetch;
  }, [executeFetch]);

  /**
   * Create stable debounced function once on mount.
   * It delegates to executeFetchRef.current so it always
   * uses the latest closure without being recreated.
   */
  useEffect(() => {
    debouncedFetchRef.current = debounce(
      (val: string, loc?: GeoCoords | null) => {
        executeFetchRef.current(val, loc);
      },
      DEBOUNCE_INTERVAL_MS
    );

    return () => {
      if (debouncedFetchRef.current?.cancel) {
        debouncedFetchRef.current.cancel();
      }
    };
  }, []);

  /**
   * Debounced search to prevent excessive API calls.
   * Stable reference — never changes.
   */
  const fetchAddressPredictions = useCallback(
    (value: string, locationBias?: GeoCoords | null) => {
      debouncedFetchRef.current?.(value, locationBias);
    },
    []
  );

  /**
   * Fetches full details when user selects an address
   *
   * @param addressId - Provider-specific address identifier
   * @returns Full address data
   */
  const fetchAddressDetails = useCallback(
    async (addressId: string): Promise<AddressData | null> => {
      if (!addressId) {
        console.error("No addressId provided to fetchAddressDetails");
        return null;
      }

      try {
        // Ensure we have a session
        if (!sessionRef.current) {
          await startNewSession();
        }

        const currentSession = sessionRef.current;

        // Start new session for next search cycle
        await startNewSession();

        // Fetch details using old session
        const addressData = await fetchAddressDetailsFromService(
          addressId,
          currentSession
        );

        return addressData;
      } catch (error) {
        console.error("Failed to fetch address details:", error);
        return null;
      }
    },
    [fetchAddressDetailsFromService, startNewSession]
  );

  /**
   * Reverse geocode coordinates to get address
   *
   * @param coords - Latitude and longitude
   * @returns Full address data
   */
  const reverseGeocode = useCallback(
    async (coords: GeoCoords): Promise<AddressData | null> => {
      if (!coords.latitude || !coords.longitude) {
        console.error("Invalid coordinates provided to reverseGeocode");
        return null;
      }

      try {
        const addressData = await reverseGeocodeFromService(coords);
        return addressData;
      } catch (error) {
        console.error("Failed to reverse geocode:", error);
        return null;
      }
    },
    [reverseGeocodeFromService]
  );

  /**
   * Forward geocode an address string to get coordinates.
   * Useful when user types/edits an address manually without selecting from suggestions.
   *
   * @param address - Address string to geocode
   * @returns Full address data with coordinates, or null
   */
  const geocodeAddress = useCallback(
    async (address: string): Promise<AddressData | null> => {
      if (!address || !address.trim()) {
        return null;
      }

      try {
        const addressData = await geocodeAddressFromService(address);
        return addressData;
      } catch (error) {
        console.error("Failed to geocode address:", error);
        return null;
      }
    },
    [geocodeAddressFromService]
  );

  /**
   * Clears search state
   */
  const clearAddressPredictions = useCallback(() => {
    setAddressPredictions([]);
    setAddressInput("");
    setLoading(false);
  }, []);

  return {
    addressPredictions,
    loading,
    fetchAddressPredictions,
    addressInput,
    fetchAddressDetails,
    reverseGeocode,
    geocodeAddress,
    clearAddressPredictions,
  };
};